


import {
  defineComponent,
  PropType,
  computed,
  reactive,
  toRef,
} from '@vue/composition-api';
import type {
  HokkaidoAirportReportContentAndRoadParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import type {
  HokkaidoAirportReportCar,
  HokkaidoAirportReport,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelSp,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import ConfirmModal from '@/components/lib/ConfirmModal.vue';
import VehicleSelectFields from '@/components/Sp/ExternalReports/HokkaidoAirport/VehicleSelectFields/index.vue';
import StandbyTypeSelectField
  from '@/components/Sp/ExternalReports/HokkaidoAirport/StandbyTypeSelectField/index.vue';
import { useCarTypeChoices } from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useCarTypeChoices';
import { isVehicleSelectionLocked } from '@/lib/externalReports/hokkaidoAirportInspection';

interface SaveWorkSituationState {
  selectedAirport: string;
  selectedCarType: string;
  selectedStandbyType: string;
  showConfirmContentAndRoadModal: boolean;
  showRevertEditContentAndRoadConfirmModal: boolean;
  hokkaidoAirportReport: HokkaidoAirportReport;
}
export default defineComponent({
  props: {
    hokkaidoAirportCar: {
      type: Object as PropType<HokkaidoAirportReportCar>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
  },
  components: {
    ConfirmModal,
    VehicleSelectFields,
    StandbyTypeSelectField,
  },
  emits: [
    'updateCommon',
    'revertScreen',
    'showErrorOnCommon',
  ],
  setup(props, { emit }) {
    const state = reactive<SaveWorkSituationState>({
      selectedAirport: props.hokkaidoAirportCar.report.airportName,
      selectedCarType: props.hokkaidoAirportCar.report.carType,
      selectedStandbyType: props.hokkaidoAirportCar.report.standbyType,
      showConfirmContentAndRoadModal: false,
      showRevertEditContentAndRoadConfirmModal: false,
      hokkaidoAirportReport: props.hokkaidoAirportCar.report,
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const airports = computed<string[]>(() => {
      return props.userSettings.choices.work_routes;
    });
    const standbyTypes = computed<string[]>(() => props.userSettings.choices.standby_types);
    const spLabels = computed<HokkaidoAirportLabelSp>(() => {
      return props.userSettings.sp;
    });
    const isSelectionLocked = computed<boolean>(() => isVehicleSelectionLocked(state.hokkaidoAirportReport));
    const { carTypes } = useCarTypeChoices(toRef(props, 'userSettings'), state);

    // 開始前点検をスキップした報告書は、空港名・車種が空欄のままでも保存できる
    const canSave = computed<boolean>(() => {
      if (isRequesting.value) return false;
      if (state.hokkaidoAirportReport.preWorkInspectionSkipped) return true;

      return !!state.selectedAirport && !!state.selectedCarType;
    });
    const isChanged = computed<boolean>(() => {
      const report = state.hokkaidoAirportReport;
      return report.airportName !== state.selectedAirport ||
        report.carType !== state.selectedCarType ||
        report.standbyType !== state.selectedStandbyType;
    });

    const tryRevertEditContentAndRoad = () => {
      if (isChanged.value) {
        state.showRevertEditContentAndRoadConfirmModal = true;
      } else {
        revertScreen();
      }
    };

    const revertScreen = () => {
      state.showRevertEditContentAndRoadConfirmModal = false;
      emit('revertScreen');
    };

    const saveContentAndRoad = async() => {
      if (!canSave.value) return;

      state.showConfirmContentAndRoadModal = false;
      const hokkaidoAirportReport = state.hokkaidoAirportReport;
      const params: HokkaidoAirportReportContentAndRoadParams = {
        airport_name: state.selectedAirport,
        car_type: state.selectedCarType,
        target_facility: hokkaidoAirportReport.targetFacility || null,
        work_areas: hokkaidoAirportReport.workAreas || null,
        standby_type: state.selectedStandbyType,
      };
      await doRequest(async() => {
        state.hokkaidoAirportReport = await hokkaidoAirportReportApi.updateHokkaidoAirportReportContentAndRoad(
          hokkaidoAirportReport.id,
          params,
        );
      }, '保存に失敗しました。再度操作を行ってください');
      emit('updateCommon', null, state.hokkaidoAirportReport);
    };

    return {
      state,
      canSave,
      isRequesting,
      isChanged,
      spLabels,
      airports,
      carTypes,
      standbyTypes,
      isSelectionLocked,
      revertScreen,
      saveContentAndRoad,
      tryRevertEditContentAndRoad,
    };
  },
});
